import { Chip, ChipProps } from "@mui/material";
import * as React from "react";

import {
  InvocationStatus,
  invocationStatusToString,
} from "@/shared/domain/actions";

const noop = () => {
  /** noop */
};

const invocationStatusToChipColor = (status?: InvocationStatus) => {
  switch (status) {
    case InvocationStatus.Queued:
      return "default";
    case InvocationStatus.Scheduled:
    case InvocationStatus.Downloading:
    case InvocationStatus.Processing:
    case InvocationStatus.Uploading:
    case InvocationStatus.Cancelled: // Fall through
      return "info";
    case InvocationStatus.Completed:
      return "success";
    case InvocationStatus.Failed:
    case InvocationStatus.Deadly: // Fall through
      return "error";
  }

  return "default";
};

// Taken from
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
function useInterval(callback = noop, delay: number | null = 1000) {
  const savedCallback = React.useRef<() => void>(callback);

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

interface InvocationStatusProps {
  onPollForStatus?: () => void;
  pollInterval?: number | null; // in milliseconds
  status: InvocationStatus;
}

export default function InvocationStatusChip(
  props: InvocationStatusProps & ChipProps,
) {
  const { onPollForStatus, pollInterval = 2000, status, ...rest } = props;

  useInterval(
    onPollForStatus,
    status < InvocationStatus.Completed && Number.isFinite(pollInterval)
      ? pollInterval
      : null,
  );

  return (
    <Chip
      color={invocationStatusToChipColor(status)}
      label={invocationStatusToString(status)}
      style={{
        borderRadius: "8px",
      }}
      {...rest}
    />
  );
}
